import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError } from "axios";
import { AlertTriangle, ChevronsRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import Alert from "@/common/components/Alert";
import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import Checkbox from "@/common/components/Checkbox";
import { DateInput } from "@/common/components/DateInput/DateInput";
import GoBackButton from "@/common/components/GoBackButton";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import Select from "@/common/components/Select";
import TextArea from "@/common/components/TextArea";
import TextField from "@/common/components/TextField";
import { Option } from "@/common/types/option";
import { FormTransformers } from "@/common/utils/formTransformers";
import { Mapper } from "@/common/services/mapper";

import { missionMaxTimesOptions } from "../constants/missionOptions.constant";
import { MISSION_PROVIDER_FALLBACK_OPTIONS } from "../constants/missionRequirementProviders.constant";
import { useMissionProducts } from "../hooks/useMissionProducts";
import { useMissionProviders } from "../hooks/useMissionProviders";
import { missionSchema, MissionSchema } from "../schemas/mission.schema";
import { MissionRewardFieldError } from "../types/rewardErrors";
import { Mission, MissionRewardType } from "../types/mission.type";
import { RequirementsSection } from "./RequirementsSection";
import { RewardsSection } from "./RewardsSection";
import { useMineCompany } from "@/features/company/hooks/useMineCompany";
import { missionErrorsTransformer } from "../utils/missionErrorsTransformer";
import { useTmaisRaffles } from "@/common/hooks/queries/useTmaisRaffles";
import { TmaisRaffleData } from "@/features/tmaisRaffles/types/tmaisRaffles.type";
import {
  RewardFormValue,
  buildMissionFormDefaultValues,
  generateMissionFieldId,
  mapMissionProvidersToOptions,
  mapMissionProductsToOptions,
  mapMissionRewardType,
  missionRewardTypeLabels
} from "../services/missionFormTransformer";

type MissionFormProps = {
  mode: "create" | "edit";
  initialValues?: Mission;
  onSubmit: (values: MissionSchema, helpers: MissionFormSubmitHelpers) => void;
  isSubmitting?: boolean;
  onCancel: () => void;
};

export type MissionFormSubmitHelpers = {
  onError: (error: AxiosError) => void;
};

export const MissionForm = ({
  mode,
  initialValues,
  onSubmit,
  isSubmitting = false,
  onCancel,
}: MissionFormProps) => {
  const submitPermission =
    mode === "create"
      ? "Establishment::Tmais::Missions::Create"
      : "Establishment::Tmais::Missions::Update";

  const form = useForm<MissionSchema>({
    resolver: zodResolver(missionSchema),
    defaultValues: buildMissionFormDefaultValues(initialValues),
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError: setFormError,
    reset,
    formState: { errors },
  } = form;

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [manualRewardsError, setManualRewardsError] = useState<string | null>(
    null,
  );
  const watchAllowMultipleTimes = watch("allow_multiple_times");
  const selectedEstablishment = watch("establishment");
  const conditions = watch("conditions") || [];
  const rewards = (watch("rewards") || []) as RewardFormValue[];
  const { data: currentCompany } = useMineCompany();
  const defaultEstablishment = useMemo(() => {
    const branch = currentCompany?.matrix_branch;
    if (!branch?.id) return null;
    const generalInfo = branch.general_information;
    const name =
      branch.display_name ||
      generalInfo?.fantasy_name ||
      generalInfo?.official_name ||
      "Estabelecimento";
    return {
      id: String(branch.id),
      display_name: String(name),
    };
  }, [
    currentCompany?.matrix_branch?.id ?? "",
    currentCompany?.matrix_branch?.display_name ?? "",
    currentCompany?.matrix_branch?.general_information?.fantasy_name ?? "",
    currentCompany?.matrix_branch?.general_information?.official_name ?? "",
  ]);

  const {
    data: providerOptions = [],
    isError: isProviderError,
    isFetched: isProviderFetched,
  } = useMissionProviders<Option[]>({
    mapper: mapMissionProvidersToOptions,
  });
  const { data: productOptions = [] } = useMissionProducts<Option[]>({
    mapper: mapMissionProductsToOptions,
  });

  const { data: rafflesOptions = [] } = useTmaisRaffles({
    mapper: Mapper.mapToOptions<TmaisRaffleData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  const conditionTypeOptions = providerOptions.length
    ? providerOptions
    : MISSION_PROVIDER_FALLBACK_OPTIONS;
  const shouldShowProviderFallbackWarning =
    !providerOptions.length && (isProviderError || isProviderFetched);

  useEffect(() => {
    reset(buildMissionFormDefaultValues(initialValues));
    setManualRewardsError(null);
  }, [initialValues, reset]);

  const missionType = watch("mission_type");

  useEffect(() => {
    if (
      missionType !== "establishment" ||
      !defaultEstablishment ||
      selectedEstablishment ||
      mode === "edit"
    ) {
      return;
    }
    setValue("establishment", defaultEstablishment);
  }, [
    missionType,
    selectedEstablishment,
    defaultEstablishment,
    setValue,
    mode,
  ]);

  useEffect(() => {
    if (!productOptions.length || !rewards.length) return;

    const rewardsMissingProduct = rewards.some(
      (reward) =>
        reward.reward_type === "product" && !(reward.reward_id || reward.product_id),
    );

    if (!rewardsMissingProduct) return;

    const assignedProductIds = new Set(
      rewards
        .filter(
          (reward) => reward.reward_type === "product",
        )
        .map((reward) => reward.reward_id || reward.product_id)
        .filter((id): id is string => Boolean(id))
        .map((id) => String(id)),
    );

    const pickNextProduct = () => {
      for (const option of productOptions) {
        const value = String(option.value);
        if (!assignedProductIds.has(value)) {
          assignedProductIds.add(value);
          return option;
        }
      }
      return null;
    };

    let hasChanges = false;
    const updatedRewards = rewards.map((reward) => {
      const productId = reward.reward_id || reward.product_id;
      if (reward.reward_type === "product" && !productId) {
        const nextProduct = pickNextProduct();
        if (!nextProduct) return reward;

        hasChanges = true;
        return {
          ...reward,
          reward_id: String(nextProduct.value),
          product_id: String(nextProduct.value),
          reward_label: String(nextProduct.label),
          product_name: String(nextProduct.label),
        };
      }

      return reward;
    });

    if (!hasChanges) return;

    setValue("rewards", updatedRewards, { shouldDirty: true });
    setManualRewardsError(null);
  }, [productOptions, rewards, setValue]);

  const handleFormSubmit = (values: MissionSchema) => {
    const onError = FormTransformers.errorsTransformer<MissionSchema>({
      setError: setFormError,
      setMessages,
      transformer: missionErrorsTransformer,
    });

    const normalizedMissionType = values.mission_type || "establishment";
    const shouldRestrictEstablishments =
      normalizedMissionType === "establishment"
        ? values.restrict_establishments
        : false;
    const normalizedAllowedEstablishments = shouldRestrictEstablishments
      ? values.allowed_establishments
      : [];
    const normalizedEstablishment =
      normalizedMissionType === "establishment" ? values.establishment : null;

    const payload: MissionSchema = {
      ...values,
      mission_type: normalizedMissionType,
      max_times: values.max_times ?? null,
      establishment: normalizedEstablishment,
      restrict_establishments: shouldRestrictEstablishments,
      allowed_establishments: normalizedAllowedEstablishments,
      rewards: values.rewards,
    };
    
    if (!payload.rewards.length) {
      setManualRewardsError(
        "Adicione pelo menos uma recompensa antes de salvar.",
      );
      return;
    }

    const itemRewards = payload.rewards.filter(
      (reward) => reward.reward_type === "product" || reward.reward_type === "tickets" || reward.reward_type === ("Tmais::Raffle" as any)
    );

    const hasMissingRewardId = itemRewards.some(
      (reward) => !(reward.reward_id || reward.product_id),
    );

    if (hasMissingRewardId) {
      setManualRewardsError(
        "Selecione uma recompensa válida ou remova os itens sem identificação.",
      );
      return;
    }

    const productRewardIds = itemRewards
      .filter(reward => reward.reward_type === "product")
      .map((reward) => reward.reward_id || reward.product_id)
      .filter((id): id is string => Boolean(id))
      .map((id) => String(id));

    const hasRepeatedProduct =
      new Set(productRewardIds).size !== productRewardIds.length;

    if (hasRepeatedProduct) {
      setManualRewardsError(
        "Não é possível cadastrar recompensas com o mesmo produto.",
      );
      return;
    }

    setManualRewardsError(null);
    onSubmit(payload, { onError });
  };

  const handleAddCondition = (
    condition: Omit<Mission["conditions"][number], "id">,
  ) => {
    setValue(
      "conditions",
      [
        ...conditions,
        { ...condition, id: generateMissionFieldId() },
      ],
      {
        shouldDirty: true,
      },
    );
  };

  const handleRemoveCondition = (id: string) => {
    setValue(
      "conditions",
      conditions.filter((condition) => condition.id !== id),
      { shouldDirty: true },
    );
  };

  const handleUpdateCondition = (
    id: string,
    data: Partial<Mission["conditions"][number]>,
  ) => {
    setValue(
      "conditions",
      conditions.map((condition) =>
        condition.id === id ? { ...condition, ...data } : condition,
      ),
      { shouldDirty: true },
    );
  };

  const handleAddReward = (payload?: {
    reward_id?: string;
    reward_label?: string;
    product_name?: string;
    quantity?: number;
    reward_type?: MissionRewardType;
  }) => {
    const resolvedType = mapMissionRewardType(payload?.reward_type as MissionRewardType) || "product";
    
    const baseReward: RewardFormValue = {
      id: generateMissionFieldId(),
      mission_reward_id: null,
      pivot_id: null,
      reward_type: resolvedType as any,
      quantity: payload?.quantity ?? 1,
      reward_id: payload?.reward_id,
      reward_label: payload?.reward_label ?? "",
      product_name: payload?.product_name ?? "",
      product_id: payload?.reward_id,
    };

    if (baseReward.reward_type === "product" && !baseReward.reward_id) {
      const defaultProduct = productOptions[0];
      if (defaultProduct) {
        baseReward.reward_id = String(defaultProduct.value);
        baseReward.product_id = String(defaultProduct.value);
        baseReward.reward_label = String(defaultProduct.label);
        baseReward.product_name = String(defaultProduct.label);
      }
    } else if (baseReward.reward_type === "tickets" && !baseReward.reward_id) {
      const defaultRaffle = rafflesOptions[0];
      if (defaultRaffle) {
        baseReward.reward_id = String(defaultRaffle.value);
        baseReward.product_id = String(defaultRaffle.value);
        baseReward.reward_label = String(defaultRaffle.label);
        baseReward.product_name = String(defaultRaffle.label);
      }
    }

    setValue("rewards", [...rewards, baseReward], { shouldDirty: true });
    setManualRewardsError(null);
  };

  const handleRemoveReward = (id: string) => {
    setValue(
      "rewards",
      rewards.filter((reward) => reward.id !== id),
      { shouldDirty: true },
    );
    setManualRewardsError(null);
  };

  const handleUpdateReward = (id: string, data: Partial<RewardFormValue>) => {
    const normalized: Partial<RewardFormValue> = { ...data };

    if ("reward_id" in data && data.reward_id) {
      const selectedItem = 
        productOptions.find((option) => String(option.value) === String(data.reward_id)) ||
        rafflesOptions.find((option) => String(option.value) === String(data.reward_id));

      normalized.product_id = String(data.reward_id);
      normalized.reward_label = selectedItem
        ? String(selectedItem.label)
        : normalized.reward_label;
      normalized.product_name = selectedItem
        ? String(selectedItem.label)
        : normalized.product_name;
    }

    setValue(
      "rewards",
      rewards.map((reward) => {
        if (reward.id !== id) return reward;
        const merged: RewardFormValue = { ...reward, ...normalized };
        const resolvedType = (normalized.reward_type || merged.reward_type) as MissionRewardType;

        if (resolvedType && resolvedType !== "product" && resolvedType !== "tickets" && resolvedType !== ("Tmais::Raffle" as any)) {
          merged.reward_id = undefined;
          merged.product_id = undefined;
          merged.product_name = undefined;
          merged.reward_label = missionRewardTypeLabels[resolvedType] || merged.reward_label;
        }

        if (resolvedType === "product" && !merged.reward_id) {
          const fallbackProduct = productOptions[0];
          if (fallbackProduct) {
            merged.reward_id = String(fallbackProduct.value);
            merged.product_id = String(fallbackProduct.value);
            merged.reward_label = String(fallbackProduct.label);
            merged.product_name = String(fallbackProduct.label);
          }
        } else if ((resolvedType === "tickets" || resolvedType === "Tmais::Raffle" as any) && !merged.reward_id) {
          const fallbackRaffle = rafflesOptions[0];
          if (fallbackRaffle) {
            merged.reward_id = String(fallbackRaffle.value);
            merged.product_id = String(fallbackRaffle.value);
            merged.reward_label = String(fallbackRaffle.label);
            merged.product_name = String(fallbackRaffle.label);
          }
        }

        return merged;
      }),
      { shouldDirty: true },
    );

    if (
      ("reward_id" in normalized && normalized.reward_id) ||
      ("reward_type" in normalized && normalized.reward_type !== "product")
    ) {
      setManualRewardsError(null);
    }
  };

  const rewardFieldErrors = Array.isArray(errors.rewards)
    ? (errors.rewards as MissionRewardFieldError[])
    : undefined;

  const rewardsListErrorFromSchema = Array.isArray(errors.rewards)
    ? undefined
    : errors.rewards?.message;

  const combinedRewardsListError =
    manualRewardsError || rewardsListErrorFromSchema;

  const requirementsErrorMessage = Array.isArray(errors.conditions)
    ? undefined
    : errors.conditions?.message;

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="flex w-full flex-col gap-6"
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GoBackButton fallback="/tmais-missions" />
          <p className="font-normal text-gray-400">Missões</p>
          <ChevronsRight className="h-6 w-6 text-gray-400" />
          <h2 className="font-bold">
            {mode === "create" ? "Cadastrar missão" : "Editar missão"}
          </h2>
        </div>

        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline-error"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
            operationKlassToCheckPermission={submitPermission}
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            {mode === "create" ? "Cadastrar missão" : "Salvar alterações"}
          </Button>
        </div>
      </div>

      <Box className="flex flex-col gap-6 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
        <p className="mb-2 text-xl font-bold">Detalhes da Missão</p>
        <input type="hidden" {...register("mission_type")} />
        <input type="hidden" {...register("status")} />
        <Controller
          control={control}
          name="establishment"
          render={() => <span className="hidden" aria-hidden="true" />}
        />
        {mode !== "create" && (
          <div className="flex flex-col gap-1 md:min-w-[200px]">
            <span className="text-sm text-gray-400">Status</span>
            <span className="text-base font-semibold text-white">
              {initialValues?.status === "disabled" ? "Inativa" : "Ativa"}
            </span>
          </div>
        )}
        <TextField
          label="Nome"
          markAsRequired
          errorMessage={errors.name?.message}
          disabled={isSubmitting}
          {...register("name")}
        />
        <TextArea
          label="Descrição"
          markAsRequired
          errorMessage={errors.description?.message}
          disabled={isSubmitting}
          className="resize-none"
          rows={3}
          {...register("description")}
        />
        <div className="mt-2 flex flex-col gap-4">
          <Checkbox
            label="Permitir que missão possa ser feita múltiplas vezes (a cada realização, os requisitos serão resetados)"
            className="text-gray-300"
            {...register("allow_multiple_times")}
            disabled={isSubmitting}
          />

          {watchAllowMultipleTimes && (
            <Controller
              control={control}
              name="max_times"
              render={({ field }) => (
                <Select
                  label="Máximo de vezes"
                  markAsRequired
                  options={missionMaxTimesOptions}
                  value={
                    field.value
                      ? missionMaxTimesOptions.find(
                          (option) => option.value === String(field.value),
                        ) || null
                      : null
                  }
                  onChange={(option) =>
                    field.onChange(
                      option ? Number((option as Option).value) : null,
                    )
                  }
                  isDisabled={isSubmitting}
                />
              )}
            />
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <DateInput
            fieldName="start_date"
            control={control as never}
            errors={errors as never}
            label="Data inicial da missão"
            markAsRequired
          />
          <DateInput
            fieldName="end_date"
            control={control as never}
            errors={errors as never}
            label="Data final da missão"
            markAsRequired
          />
        </div>
      </Box>

      {shouldShowProviderFallbackWarning && (
        <Alert variant="warning" Icon={AlertTriangle}>
          Nao foi possivel carregar os tipos de requisito disponiveis no
          servidor. Carregamos a lista padrao; confirme se voce possui a
          permissao
          'Establishment::Tmais::MissionRequirements::AvailableProviders' ou
          tente novamente mais tarde.
        </Alert>
      )}

      <RequirementsSection
        conditions={conditions}
        onAddCondition={handleAddCondition}
        onRemoveCondition={handleRemoveCondition}
        onUpdateCondition={handleUpdateCondition}
        isSubmitting={isSubmitting}
        conditionTypeOptions={conditionTypeOptions}
        errorMessage={requirementsErrorMessage}
      />

      <RewardsSection
        rewards={rewards}
        onAddReward={handleAddReward}
        onUpdateReward={handleUpdateReward}
        onRemoveReward={handleRemoveReward}
        isSubmitting={isSubmitting}
        rewardErrors={rewardFieldErrors}
        rewardsListError={combinedRewardsListError}
        productOptions={productOptions}
        rafflesOptions={rafflesOptions}
        missionType={missionType}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages as string[]}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  );
};